import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { useDidUpdate } from 'rooks';
import { chain } from 'lodash';
import moment from 'moment';
import { Button, Spin, Row, Col } from 'antd';
import {
  Pie,
  Sector,
  Legend,
  Tooltip,
  Surface,
  Symbols,
  PieChart,
  ResponsiveContainer,
} from 'recharts';

import ChartDateRangePicker, {
  DateRangeFilterType,
} from '../ChartDateRangePicker';
import {
  getDateWithoutTime,
  getDateRangeTillLastSunday,
} from 'utils/dateUtils';
import {
  SortingEnum,
  SummaryTrendByCustomerReportViewModel,
} from 'api/backofficeApiClient';
import { DateParams } from 'utils/filterUtils';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { CustomerBillingProfitSortFieldEnum } from '../../enums';
import { changeOtherChartItemIndex, dateRangeDurationInWeeks } from '../utils';
import { getCustomerMixProfitChartData } from 'redux/reducers/homeDashboardSlice';

import styles from '../../UserHomePage.module.less';

interface ChartColorSettings {
  id: string;
  fill: string;
  fillColor: string;
  gradientStartColor: string;
  gradientEndColor: string;
}

interface CustomerMixProfitChartItem
  extends ChartColorSettings,
    SummaryTrendByCustomerReportViewModel {}

const chartPageSize = 5;
const animationDuration = 1500;
const pieDefaultProps = {
  dataKey: 'value',
  animationDuration,
  width: 192,
  height: 192,
  innerRadius: 50,
  outerRadius: 110,
};

const chartColorSettings: ChartColorSettings[] = [
  {
    id: 'gradCustomerMixProfitFirst',
    fill: 'url(#gradCustomerMixProfitFirst)',
    fillColor: '#2CC5F7',
    gradientStartColor: '#DAE5F9',
    gradientEndColor: '#2CC5F7',
  },
  {
    id: 'gradCustomerMixProfitSecond',
    fill: 'url(#gradCustomerMixProfitSecond)',
    fillColor: '#FABAFF',
    gradientStartColor: '#E9DCE9',
    gradientEndColor: '#FABAFF',
  },
  {
    id: 'gradCustomerMixProfitThird',
    fill: 'url(#gradCustomerMixProfitThird)',
    fillColor: '#B2FFCF',
    gradientStartColor: '#B5C4FF',
    gradientEndColor: '#B2FFCF',
  },
  {
    id: 'gradCustomerMixProfitFourth',
    fill: 'url(#gradCustomerMixProfitFourth)',
    fillColor: '#AA99CD',
    gradientStartColor: '#B3C2EB',
    gradientEndColor: '#AA99CD',
  },
  {
    id: 'gradCustomerMixProfitOther',
    fill: 'url(#gradCustomerMixProfitOther)',
    fillColor: '#EE5C69',
    gradientStartColor: '#FCBA9D',
    gradientEndColor: '#EE5C69',
  },
];

const getFormattedProportionData = (
  proportionData: CustomerMixProfitChartItem,
) =>
  `${proportionData.customerName}: ${(
    proportionData.percentageOfGreatTotal as number
  ).toFixed(2)}%`;

const renderTooltip = (props: any) => {
  if (props.active) {
    const chartItem: CustomerMixProfitChartItem = props.payload[0].payload;

    return (
      <div className={styles.tooltip}>
        <span
          className={styles.colorBox}
          style={{
            backgroundColor: chartItem.fillColor,
          }}
        />
        <span>{getFormattedProportionData(chartItem)}</span>
      </div>
    );
  }

  return null;
};

const renderActiveShape = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
    props;

  return (
    <g id={props.chartId}>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius - 3}
        outerRadius={outerRadius + 3}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        filter="url(#customerMixProfitChartShadow)"
      />
    </g>
  );
};

const renderLegend = (props: any) => {
  const payload: CustomerMixProfitChartItem[] = props.payload;

  return (
    <ul className={styles.legend}>
      {payload.map((entry, index) => (
        <li className={styles.legendItem} key={`legend-item-${index}`}>
          <Surface width={12} height={12}>
            <Symbols
              cx={6}
              cy={6}
              size={100}
              type="circle"
              fill={entry.fillColor}
            />
          </Surface>

          <span className={styles.legendName}>
            {getFormattedProportionData(entry)}
          </span>
        </li>
      ))}
    </ul>
  );
};

const getFormattedChartData = (
  data: SummaryTrendByCustomerReportViewModel[],
): CustomerMixProfitChartItem[] => {
  const sortedChartData = chain(data)
    .filter((item) => !!item.percentageOfGreatTotal)
    .orderBy((item) => item.percentageOfGreatTotal, 'desc')
    .value();

  const updatedIndexChartData = changeOtherChartItemIndex(
    sortedChartData,
    'customerName',
  );

  return updatedIndexChartData.map((chartItem, index) => ({
    ...chartItem,
    ...chartColorSettings[index],
    value: chartItem.percentageOfGreatTotal,
  }));
};

export default function CustomerMixProfitChart() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const {
    mixProfitStart: startDate,
    mixProfitEnd: endDate,
    ...restQueryParams
  } = queryString.parse(location.search);
  const defaultDateFilter: DateParams = getDateRangeTillLastSunday(
    dateRangeDurationInWeeks,
  );
  const initialDateFilter: DateRangeFilterType =
    startDate && endDate
      ? {
          startDate: moment(startDate, 'MM/DD/YYYY'),
          endDate: moment(endDate, 'MM/DD/YYYY'),
        }
      : defaultDateFilter;
  const [dateFilter, setDateFilter] =
    useState<DateRangeFilterType>(initialDateFilter);
  const [isInitialAnimationActive, setIsInitialAnimationActive] =
    useState<boolean>(true);
  const [chartData, setChartData] = useState<
    SummaryTrendByCustomerReportViewModel[]
  >([]);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>();
  const chartId = useRef(`chart-id-${Date.now()}`);
  const formattedChartData = useMemo(
    () => getFormattedChartData(chartData),
    [chartData],
  );
  const isDisabled = chartData.every(
    (chartItem) => !chartItem.percentageOfGreatTotal,
  );

  useEffect(() => {
    setTimeout(() => {
      setIsInitialAnimationActive(false);
    }, animationDuration + 100);
  }, []);

  useDidUpdate(() => {
    const queryData = {
      mixProfitStart: dateFilter?.startDate
        ? dateFilter.startDate.format('MM/DD/YYYY')
        : undefined,
      mixProfitEnd: dateFilter?.endDate
        ? dateFilter.endDate.format('MM/DD/YYYY')
        : undefined,
      ...restQueryParams,
    };

    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
    // eslint-disable-next-line
  }, [dateFilter]);

  const loadCustomerMixProfitChartData = useCallback(async () => {
    setIsChartLoading(true);

    const result = await dispatch(
      getCustomerMixProfitChartData({
        pageSize: chartPageSize,
        sortField: CustomerBillingProfitSortFieldEnum.TotalPercentage,
        sortType: SortingEnum.Desc,
        startDate: dateFilter?.startDate
          ? getDateWithoutTime(dateFilter.startDate)
          : undefined,
        endDate: dateFilter?.endDate
          ? getDateWithoutTime(dateFilter.endDate)
          : undefined,
      }),
    );

    if (getCustomerMixProfitChartData.fulfilled.match(result)) {
      setChartData(result.payload || []);
    } else {
      setChartData([]);
    }

    setIsChartLoading(false);
  }, [dispatch, dateFilter]);

  useEffect(() => {
    loadCustomerMixProfitChartData();
  }, [loadCustomerMixProfitChartData]);

  const onPieEnter = (data: any, index: number) => {
    setActiveIndex(index);
  };

  return (
    <div className={styles.flexChart}>
      <div className={styles.title}>
        <Link to="/customer-trend-weekly-profit-report-details">
          Customer Mix - Profit
        </Link>
        <Button
          type="primary"
          onClick={() =>
            navigate('/customer-trend-weekly-profit-report-details')
          }
        >
          View Details
        </Button>
      </div>
      <div className={styles.cardWrapper}>
        <div className={styles.chartWrapper}>
          <Row justify="end">
            <Col>
              <div className={styles.selectBox}>
                <ChartDateRangePicker
                  pickerKey="customerMixProfitFilterByDate"
                  value={dateFilter}
                  onChange={setDateFilter}
                />
              </div>
            </Col>
          </Row>

          <Spin spinning={isChartLoading}>
            <div
              className={styles.pieChartWrapper}
              style={{
                pointerEvents: isInitialAnimationActive ? 'none' : 'auto',
              }}
            >
              <ResponsiveContainer width="100%">
                <PieChart>
                  <defs>
                    {chartColorSettings.map((chartSet) => (
                      <radialGradient
                        id={chartSet.id}
                        key={chartSet.id}
                        gradientUnits="userSpaceOnUse"
                        cx="25%"
                        r="100%"
                      >
                        <stop
                          offset="0%"
                          stopColor={chartSet.gradientStartColor}
                        />
                        <stop
                          offset="50%"
                          stopColor={chartSet.gradientEndColor}
                        />
                      </radialGradient>
                    ))}
                    <filter
                      id="customerMixProfitChartShadow"
                      x="0"
                      y="0"
                      width="150%"
                      height="150%"
                      filterUnits="userSpaceOnUse"
                    >
                      <feDropShadow
                        dx="0"
                        dy="0"
                        stdDeviation="3"
                        floodColor="#4a6778"
                      />
                    </filter>
                  </defs>
                  {isDisabled && !isChartLoading && (
                    <Pie
                      data={[
                        {
                          value: 1,
                          fill: '#D8D8D8',
                        },
                      ]}
                      {...pieDefaultProps}
                    />
                  )}
                  {!isDisabled && (
                    <Pie
                      activeIndex={activeIndex}
                      activeShape={(props) =>
                        renderActiveShape({
                          ...props,
                          chartId: chartId.current,
                        })
                      }
                      data={formattedChartData}
                      onMouseEnter={onPieEnter}
                      onMouseLeave={() => setActiveIndex(undefined)}
                      {...pieDefaultProps}
                    />
                  )}
                  <Legend
                    layout="vertical"
                    payload={formattedChartData as any}
                    wrapperStyle={{
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '41%',
                      minWidth: '200px',
                    }}
                    align="right"
                    verticalAlign="middle"
                    content={renderLegend}
                  />
                  {!isDisabled && <Tooltip content={renderTooltip} />}
                  <g>
                    <use
                      href={`#${chartId.current}`}
                      style={{
                        pointerEvents: 'none',
                      }}
                    />
                  </g>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Spin>
        </div>
      </div>
    </div>
  );
}
